import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { ToastrService } from 'ngx-toastr';
import { GetProctoringSessionReportsPagedPayload, ProctoringSessionReport, SessionReportFilters } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctoring-session';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, Subscription, tap } from 'rxjs';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';


@Component({
	selector: 'app-proctoring-candidates',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule, MatTooltipModule, HasPermissionDirective,
		FormsModule, RouterModule],
	templateUrl: './proctoring-candidates.component.html',
	styleUrl: './proctoring-candidates.component.scss'
})
export class ProctoringCandidatesComponent implements OnInit, OnDestroy {

	searchText: string = '';
	showColumnDropdown = false;
	pageSize = 10;
	currentPage = 1;
	totalPages = 5
	sessionDateInput: string = '';
	showIntegrityPopup = false;
	minIntegrity: number | null = null;
	maxIntegrity: number | null = null;
	selectedAssessment: string = 'All Assessments';
	selectedStatus: string = 'All Statuses';
	isLoading = false;
	isInitialLoad = true;
	subscription = new Subscription();
	candidateData: ProctoringSessionReport[] = [];
	totalCandidate = 0;
	sortField = 'createdAt';
	sortOrder: 'asc' | 'desc' = 'desc';
	searchSubject = new Subject<string>();
	@Output() totalCandidateChange = new EventEmitter<number>();
	PermissionForStudents = PermissionForStudents;
	minDate = '2020-01-01';
	maxDate = new Date().toISOString().split('T')[0];


	assessments: string[] = [
		'All Assessments',
		'Backend Engineer — Screening',
		'Frontend Engineer',
		'Java Developer Assessment',
		'Data Analyst Certification',
		'QA Automation Engineer'
	];

	statuses: { label: string, value: string }[] = [
		{ label: 'All Statuses', value: 'All Statuses' },
		{ label: 'Active', value: 'ACTIVE' },
		{ label: 'Completed', value: 'COMPLETED' },
		{ label: 'Requested', value: 'REQUESTED' },
		{ label: 'Terminated', value: 'Terminated' },
		{ label: 'Abandoned', value: 'ABANDONED' }
	];


	constructor(
		private router: Router,
		private proctoringService: ProctoringService,
		private toastr: ToastrService
	) { }


	ngOnInit(): void {
		this.getProctoringCandidate();
		this.subscription.add(
			this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(value => {
				this.searchText = value;
				this.currentPage = 1;
				this.getProctoringCandidate();
			})
		);

	}



	toggleIntegrityPopup(): void {
		this.showIntegrityPopup = !this.showIntegrityPopup;
	}


	applyIntegrityFilter() {
		this.showIntegrityPopup = false;
		this.applyFilters();
	}


	resetIntegrityFilter() {
		this.minIntegrity = null;
		this.maxIntegrity = null;
		this.applyFilters();
	}


	getProctoringCandidate() {
		this.isLoading = true;
		const filters: SessionReportFilters = {};
		if (this.selectedAssessment && this.selectedAssessment !== 'All Assessments') {
			filters.proctoringEvaluationOnId = {
				equals: this.selectedAssessment
			};
		}
		if (this.selectedStatus && this.selectedStatus !== 'All Statuses') {
			filters.sessionStatus = {
				equals: this.selectedStatus
			};
		}
		if (this.sessionDateInput) {
			filters.sessionDate = {
				between: [
					`${this.sessionDateInput}T00:00:00.000Z`,
					`${this.sessionDateInput}T23:59:59.999Z`
				]
			};
		}
		if (this.minIntegrity != null || this.maxIntegrity != null) {
			filters.integrityScore = {
				gte: this.minIntegrity ?? 0,
				lte: this.maxIntegrity ?? 100
			};
		}
		const payload: GetProctoringSessionReportsPagedPayload = {
			page: this.currentPage,
			pageSize: this.pageSize,
			filters,
			sort: [
				{
					field: this.sortField,
					order: this.sortOrder === 'asc' ? 'ASC' : 'DESC'
				}
			],
			all: false
		};

		if (this.searchText && this.searchText.trim() !== '') {
			payload.search = this.searchText;
		}

		this.subscription.add(
			this.proctoringService.getProctoringCandidate(payload).pipe(
				tap((response) => {
					if (response.success) {
						this.candidateData = response.data.data;
						this.totalCandidate = response.data.pagination.total;
						this.currentPage = response.data.pagination.page;
						this.pageSize = response.data.pagination.pageSize;
						this.totalPages = response.data.pagination.totalPages;
						this.totalCandidateChange.emit(this.totalCandidate);
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
					this.isInitialLoad = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}



	columns = [
		{
			name: 'Candidate',
			visible: true
		},
		{
			name: 'Assessment Code',
			visible: true
		},
		{
			name: 'Assessment Name',
			visible: true
		},
		{
			name: 'Status',
			visible: true
		},
		{
			name: 'Integrity Score',
			visible: true
		},
		{
			name: 'Violations',
			visible: true
		},
		{
			name: 'Assessment Date',
			visible: true
		},
		{
			name: 'Actions',
			visible: true
		}
	];


	toggleColumnDropdown() {
		this.showColumnDropdown = !this.showColumnDropdown;
	}


	@HostListener('document:click', ['$event'])
	clickOutside(event: MouseEvent) {
		const target = event.target as HTMLElement;

		if (!target.closest('.column-filter')) {
			this.showColumnDropdown = false;
		}
	}


	goToProtoCandidateDetailPage(sessionId: string) {
		this.router.navigate(['proctoring-dashboard/candidate-report', sessionId]);
	}


	onSearch(event: Event) {
		const value = (event.target as HTMLInputElement).value;
		this.searchSubject.next(value);
	}


	applyFilters() {
		this.currentPage = 1;
		this.getProctoringCandidate();
	}


	changePageSize() {
		this.currentPage = 1;
		this.getProctoringCandidate();
	}


	previousPage() {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.getProctoringCandidate();
		}
	}


	nextPage() {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.getProctoringCandidate();
		}
	}

	goToPage(page: number) {
		if (page >= 1 && page <= this.totalPages) {
			this.currentPage = page;
			this.getProctoringCandidate();
		}
	}

	get pagesArray(): number[] {
		return Array.from({ length: this.totalPages }, (_, i) => i + 1);
	}


	get startRecord(): number {
		return this.totalCandidate === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
	}

	get endRecord(): number {
		return Math.min(this.currentPage * this.pageSize, this.totalCandidate);
	}


	sortBy(field: string) {
		if (this.sortField === field) {
			this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortField = field;
			this.sortOrder = 'asc';
		}

		this.currentPage = 1;
		this.getProctoringCandidate();
	}

	preventNonNumericalInput(event: KeyboardEvent): void {
		const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
		if (allowedKeys.includes(event.key)) {
			return;
		}
		if (event.key === '-' || event.key === '.' || event.key === 'e' || event.key === '+') {
			event.preventDefault();
		}
	}


	getValidationError(): string {
		if (this.minIntegrity !== null && this.minIntegrity < 0) return "Score cannot be negative.";
		if (this.maxIntegrity !== null && this.maxIntegrity < 0) return "Score cannot be negative.";
		if (this.minIntegrity !== null && this.minIntegrity > 100) return "Score cannot exceed 100.";
		if (this.maxIntegrity !== null && this.maxIntegrity > 100) return "Score cannot exceed 100.";
		if (this.minIntegrity !== null && this.maxIntegrity !== null && this.minIntegrity > this.maxIntegrity) {
			return "Min score cannot be greater than Max.";
		}
		return "";
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}
